import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiClient } from '@/api/client';
import { clearAllFormDrafts } from '@/composables/useFormDraft';
import { dimenticaMieiCentri } from '@/composables/useMieiCentri';
import { azzeraHubVoci } from '@/composables/hubVociStato';
import { useFiltriListaStore } from '@/stores/filtriLista';
import { dimenticaSessioneFirma } from '@/modules/firmaremota/stores/firmaSessione';
import type { components } from '@/api/generated/schema';

/** Tipo generato dal contratto OpenAPI (`components['schemas']['MeResponse']`). */
export type AuthMe = components['schemas']['MeResponse'];

export const useAuthStore = defineStore('auth', () => {
  const me = ref<AuthMe | null>(null);
  const loading = ref(false);
  const loaded = ref(false);

  const isAuthenticated = computed(() => me.value !== null);

  /** Super-admin di piattaforma: ogni account super-admin, anche collegato in passpartout (stessa regola del backend). */
  const isPlatformSuperAdmin = computed(() => me.value?.isSuperAdmin === true);

  /**
   * Verifica se l'utente ha un permesso.
   * Solo cosmetico (per nascondere menu/bottoni). Il check vero è server-side.
   */
  function has(permission: string): boolean {
    if (!me.value) return false;
    // PLATFORM_SUPERADMIN è un gate ESCLUSIVO dei super-admin (anche in passpartout): non passa per un non-super,
    // anche se il permesso finisse tra i suoi permessi effettivi per derivazioni legacy.
    if (permission === 'PLATFORM_SUPERADMIN') return me.value.isSuperAdmin === true;
    if (me.value.isSuperAdmin) return true;
    return (me.value.permissions ?? []).includes(permission);
  }

  /** True se l'utente ha accesso a un dato centro (UX only). */
  function canAccessCenter(centerId: number): boolean {
    if (!me.value) return false;
    if (me.value.isSuperAdmin) return true;
    return (me.value.accessibleCenters ?? []).includes(centerId);
  }

  /**
   * Esegue il login (1ª fase: username+password).
   * - Se l'utente ha l'OTP (2FA) attivo, il backend NON completa il login e risponde
   *   `{ otpRequired: true }`: nessun cookie di accesso, va chiamato {@link verifyOtp}.
   * - Altrimenti popola `me` e restituisce il redirectUrl ('/' = portale Vue, '/home.xhtml' = Seam).
   */
  async function login(
    username: string,
    password: string,
  ): Promise<{ otpRequired: boolean; redirectUrl?: string }> {
    loading.value = true;
    try {
      const { data } = await apiClient.post<{ redirectUrl?: string; otpRequired?: boolean }>(
        '/auth/login',
        { username, password },
      );
      if (data.otpRequired) {
        return { otpRequired: true };
      }
      await fetchMe();
      return { otpRequired: false, redirectUrl: data.redirectUrl ?? '/' };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Completa il login a due fasi verificando il codice OTP (2ª fase). Il token intermedio viaggia
   * nel cookie HttpOnly `otp_pending`. Se valido, il backend emette i cookie di accesso.
   */
  async function verifyOtp(code: string): Promise<string> {
    loading.value = true;
    try {
      const { data } = await apiClient.post<{ redirectUrl?: string }>('/auth/otp/verify', { code });
      await fetchMe();
      return data.redirectUrl ?? '/';
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      me.value = null;
      loaded.value = true;
      // pulisce le bozze form (sessionStorage) per non lasciare dati personali al prossimo utente
      clearAllFormDrafts();
      // i centri sono della persona: il prossimo utente deve ricaricare i suoi
      dimenticaMieiCentri();
      // i filtri delle liste sono della persona che esce
      useFiltriListaStore().svuota();
      // la sessione di firma digitale è della persona che esce
      dimenticaSessioneFirma();
      // le voci dell'hub Configurazione sono di chi esce
      azzeraHubVoci();
    }
  }

  async function fetchMe(): Promise<void> {
    try {
      const { data } = await apiClient.get<AuthMe>('/auth/me');
      // Cambio di persona (es. passpartout): i centri in memoria erano di un altro utente.
      if (me.value && me.value.userAccountId !== data.userAccountId) {
        dimenticaMieiCentri();
        useFiltriListaStore().svuota();
        dimenticaSessioneFirma();
      }
      me.value = data;
      // Ora che siamo autenticati, ricarica il bundle i18n: pre-login l'endpoint
      // restituisce solo i namespace globali (auth/common), quindi le label dei moduli
      // (es. personale.list.title) sarebbero mostrate come chiavi grezze fino a un refresh.
      // Import dinamico per evitare cicli i18n↔apiClient. Best-effort.
      try {
        const { loadLocaleMessages, storedLocale } = await import('@/i18n');
        await loadLocaleMessages(storedLocale());
      } catch {
        /* i18n best-effort: in caso di errore si resta sul bundle in cache */
      }
    } catch {
      me.value = null;
    } finally {
      loaded.value = true;
    }
  }

  /** Garantisce che lo store sia inizializzato (chiama fetchMe se serve). */
  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return;
    await fetchMe();
  }

  /**
   * Cambia la password dell'utente autenticato.
   * POST /auth/change-password
   * In caso di errore 400 (es. password attuale errata, password troppo corta),
   * rilancia con il messaggio restituito dal server.
   */
  async function changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
  }

  /**
   * Esce dalla modalità passpartout restando loggati come lo stesso utente.
   * POST /auth/passpartout/exit ri-emette il cookie senza il flag passpartout;
   * poi ricarichiamo /auth/me così lo store riflette passpartout=false.
   */
  /**
   * Esce dall'impersonazione.
   *
   * @param torna true (default) = torna all'operatore che ha impersonato; false = resta
   *              nell'utente impersonato e perde solo la chiave, per vedere il portale
   *              esattamente come lo vede lui.
   */
  async function exitPasspartout(torna = true): Promise<void> {
    await apiClient.post(`/auth/passpartout/exit?torna=${torna}`);
    await fetchMe();
  }

  return {
    me,
    isAuthenticated,
    isPlatformSuperAdmin,
    loading,
    has,
    canAccessCenter,
    login,
    verifyOtp,
    logout,
    fetchMe,
    ensureLoaded,
    changePassword,
    exitPasspartout,
  };
});
